/**
 * @fileoverview Composable for video player functionality
 * @module composables/useVideoPlayer
 *
 * Manages video message display, minimize/expand states,
 * and video selection in the chat window.
 *
 * @vue3migration - Phase 3E: Extracted from ChatWindow/Index.vue
 */
import { ref, computed, type ComputedRef } from 'vue'
import * as _ from 'lodash-es'

/**
 * Chat message interface for video messages
 */
interface ChatMessage {
  id: string
  type: 'message' | 'reply' | 'postback' | 'agent'
  timestamp: Date | string
  data: {
    content: Array<{
      text?: string
      options?: {
        mode?: string
        [key: string]: any
      }
    }>
  }
}

/**
 * Options for useVideoPlayer composable
 */
interface UseVideoPlayerOptions {
  /** Sorted messages from chat */
  sortedMessages: ComputedRef<ChatMessage[]>
  /** Callback to scroll to bottom after video state changes */
  scrollToBottom?: () => void
}

/**
 * Composable for video player functionality
 *
 * @param options - Dependencies and callbacks
 * @returns Video player state and control methods
 *
 * @example
 * ```ts
 * const {
 *   selectedVideo,
 *   isMinimizeVideo,
 *   lastVideo,
 *   showTextInputVideo,
 *   handleMinimizeVideo,
 *   handleExpandVideo,
 *   resetSelectedVideo,
 * } = useVideoPlayer({
 *   sortedMessages: computed(() => sortedMessages.value),
 *   scrollToBottom: () => scrollToBottom(),
 * })
 * ```
 */
export function useVideoPlayer(options: UseVideoPlayerOptions) {
  const { sortedMessages, scrollToBottom } = options

  // ====================
  // State
  // ====================

  /** Currently selected video message */
  const selectedVideo = ref<ChatMessage | null>(null)

  /** Whether video is minimized */
  const isMinimizeVideo = ref(false)

  // ====================
  // Computed
  // ====================

  /**
   * Last video message from bot replies
   * Returns the last bot message if it's a video message, false otherwise
   */
  const lastVideo = computed(() => {
    if (sortedMessages.value.length === 0) return false
    const botMessages = sortedMessages.value.filter(
      (message) => message.type === 'reply'
    )
    const lastEl = _.last(botMessages) || ({} as ChatMessage)
    return lastEl?.data?.content?.[0]?.options?.mode === 'video' ? lastEl : false
  })

  /**
   * Whether to show text input when video is present
   * Shows input only when video is minimized
   */
  const showTextInputVideo = computed(() => {
    if (lastVideo.value || selectedVideo.value) {
      return isMinimizeVideo.value
    }
    return true
  })

  // ====================
  // Methods
  // ====================

  /**
   * Set video minimize state
   * Clears selected video and scrolls to bottom when minimizing
   *
   * @param minimize - Whether to minimize (true) or expand (false), defaults to true
   */
  function handleMinimizeVideo(minimize = true) {
    isMinimizeVideo.value = minimize
    if (minimize) {
      selectedVideo.value = null
      // nextTick scroll is handled by component
      scrollToBottom?.()
    }
  }

  /**
   * Expand video player with selected video
   *
   * @param value - Video message to display
   */
  function handleExpandVideo(value: ChatMessage) {
    selectedVideo.value = value
    isMinimizeVideo.value = false
  }

  /**
   * Reset selected video state
   * Clears selection and scrolls to bottom
   */
  function resetSelectedVideo() {
    selectedVideo.value = null
    // nextTick scroll is handled by component
    scrollToBottom?.()
  }

  return {
    // State
    selectedVideo,
    isMinimizeVideo,

    // Computed
    lastVideo,
    showTextInputVideo,

    // Methods
    handleMinimizeVideo,
    handleExpandVideo,
    resetSelectedVideo,
  }
}
